/**
 * WEB-2054 — ДВЕРЬ от ядра рендерера к ленивым кускам.
 *
 * Ядро (`PublicBlockRenderer`) рисует виды, которые есть почти в любой воронке,
 * и на всём остальном отдаёт блок сюда. Раньше на этом месте стоял СТАТИЧЕСКИЙ
 * импорт `PublicBlockRendererExtended`, и вместе с ним посетителю ехали все
 * редкие виды разом (196 КБ несжатого текста на 60 веток). Теперь дверь
 * спрашивает у карты, какой кусок рисует этот вид, и берёт только его.
 *
 * ОДИН ТИП ЭЛЕМЕНТА В ОБОИХ СОСТОЯНИЯХ (починка ревизии, Р2). Первая редакция
 * возвращала два РАЗНЫХ элемента: пока куска нет — `Boundary > Suspense >
 * Lazy`, а как только кусок на руках — компонент куска напрямую. Для React это
 * смена типа, то есть размонтирование и новое монтирование блока: слетал фокус
 * у поля ввода (на телефоне закрывалась клавиатура), единица веса/роста
 * откатывалась к умолчанию поверх уже записанного ответа, сворачивался
 * аккордеон, перезапускалось видео, очищался выбранный файл. Перерисовка
 * приходит на КАЖДЫЙ ответ покупателя, поэтому попасть в это мог любой редкий
 * блок. Теперь дерево одно и то же всегда: `Boundary > Suspense >
 * PublicBlockChunk`, а ожидание куска выражено приостановкой (Suspense), а не
 * другой веткой разметки.
 *
 * ТРИ СОСТОЯНИЯ:
 *  1. кусок на руках (грелка по составу воронки успела либо кабинет положил его
 *     статически) — рисуем СИНХРОННО, тем же кадром, что и до разреза;
 *  2. куска нет — приостанавливаемся. Пусто вместо блока на долю секунды
 *     честнее спиннера на каждом блоке: ожидание здесь — редкий случай;
 *  3. кусок не приехал вовсе (аутаж CDN после однократной перезагрузки
 *     `retryImport`) — граница падения оставляет пусто на месте блока, отказ
 *     уходит отчётом на сервер, и страница живёт дальше. Без границы отказ
 *     одного редкого блока уносил бы всю воронку в белый экран — вместе с
 *     кнопкой оплаты.
 */
import * as React from 'react';
import { Suspense } from 'react';
import {
  publicBlockChunkOf,
  type PublicBlockChunkName,
} from '@/player/publicBlockChunkMap';
import {
  getLoadedPublicBlockChunk,
  loadPublicBlockChunk,
  type PublicBlockChunkComponent,
} from '@/player/publicBlockChunkLoaders';
import type { PublicExtendedBlockProps } from '@/player/publicExtendedBlockProps';
import { withExtendedBlockShell } from './extendedBlockShell';
import { reportClientCrash } from '@/components/common/reportClientCrash';

/**
 * Ожидание куска: промис, который ВСЕГДА успешно завершается. Отказ он не
 * пробрасывает, а запоминает — иначе React перерисовывал бы приостановленный
 * блок на каждое отклонение того же промиса и крутил бы этот круг без конца.
 * Отказ показывается следующей отрисовкой, обычным броском ошибки в границу.
 */
const waiting = new Map<PublicBlockChunkName, Promise<void>>();
const failure = new Map<PublicBlockChunkName, unknown>();
/**
 * Приехавший кусок дверь помнит САМА, а не только спрашивает у загрузчика.
 * Так делала и `React.lazy`, которую этот компонент заменил: без своей памяти
 * приостановка после уже разрешённого промиса повторялась бы бесконечно, если
 * бы кэш загрузчика по какой-то причине оказался пуст.
 */
const arrived = new Map<PublicBlockChunkName, PublicBlockChunkComponent>();
/**
 * ОДИН ОТЧЁТ НА КУСОК ЗА ВИЗИТ (свежий взгляд, Н2). Граница падения стоит у
 * КАЖДОГО редкого блока, а кусок один на десятки видов: экран с десятью
 * такими блоками слал десять запросов `/health/client-error` по одной причине
 * и десятикратно раздувал счётчик падений фронта в «Пульсе» — одна поломка
 * выглядела как десять. Отметка живёт рядом с памятью отказов и гасится тем же
 * сбросом.
 *
 * ЦЕНА ОТМЕТКИ И ПОЧЕМУ ОНА НЕ ВЕЧНАЯ (свежий взгляд пачки 36). Отметка «за
 * визит» молчала бы и о ВТОРОЙ, другой поломке того же куска: кусок не
 * приехал (отчёт ушёл), потом приехал, а потом упал уже внутри себя — про это
 * «Пульс» не узнал бы вовсе. Поэтому приезд куска отметку снимает: следующая
 * поломка того же куска — новая причина, и о ней отчитываются заново.
 */
const reported = new Set<PublicBlockChunkName>();

/** Только для тестов: забыть ожидания, приехавшие куски, отказы и отчёты. */
export function __resetPublicBlockChunkLazyCacheForTests(): void {
  waiting.clear();
  failure.clear();
  arrived.clear();
  reported.clear();
}

interface ChunkProps extends PublicExtendedBlockProps {
  chunk: PublicBlockChunkName;
}

/**
 * ОДИН И ТОТ ЖЕ тип элемента в любом состоянии куска — в этом весь смысл
 * компонента (см. шапку файла). Пока куска нет, он приостанавливается; когда
 * кусок на руках — рисует его. Ни одно из состояний не меняет тип элемента
 * выше по дереву, значит React ничего не пересобирает.
 */
function PublicBlockChunk({
  chunk,
  ...props
}: ChunkProps): React.ReactElement | null {
  const Loaded = getLoadedPublicBlockChunk(chunk) ?? arrived.get(chunk) ?? null;
  if (Loaded) return <Loaded {...props} />;

  const failed = failure.get(chunk);
  if (failed) throw failed;

  let pending = waiting.get(chunk);
  if (!pending) {
    pending = loadPublicBlockChunk(chunk).then(
      (component: PublicBlockChunkComponent) => {
        arrived.set(chunk, component);
        // Кусок ожил — прежний отчёт закрыт. Следующая его поломка будет
        // ДРУГОЙ причиной, и о ней надо сказать (см. док у `reported`).
        reported.delete(chunk);
      },
      (error: unknown) => {
        failure.set(
          chunk,
          error instanceof Error ? error : new Error(String(error)),
        );
      },
    );
    waiting.set(chunk, pending);
  }
  throw pending;
}

interface BoundaryProps {
  chunk: PublicBlockChunkName;
  children: React.ReactNode;
}

/**
 * Граница падения на ОДИН блок. Своя, а не общая на страницу: смысл в том,
 * чтобы не приехавший кусок стоил ровно своего блока.
 *
 * Починка ревизии: граница стоит на ОБОИХ путях (и когда кусок ещё едет, и
 * когда он уже на руках) — падение внутри самого блока после загрузки тоже не
 * имеет права уносить страницу. И отказ больше не пропадает молча: он уходит
 * тем же отчётом на сервер, что у общей границы публичных страниц
 * (`PublicErrorBoundary`), иначе про мёртвый кусок никто бы не узнал.
 */
class PublicBlockChunkBoundary extends React.Component<
  BoundaryProps,
  { failed: boolean }
> {
  constructor(props: BoundaryProps) {
    super(props);
    this.state = { failed: false };
  }

  static getDerivedStateFromError(): { failed: boolean } {
    return { failed: true };
  }

  /**
   * ОТКАЗ ЗАБЫВАЕТСЯ ЗДЕСЬ (свежий взгляд пачки 35, С1). Дверь держала отказ
   * куска вечно: первый же промах CDN — и вид блока пуст до конца сессии, даже
   * когда сеть давно вернулась. Забыть его РАНЬШЕ (прямо на броске) нельзя —
   * React повторяет отрисовку приостановленного поддерева, и пустая память
   * означала бы бесконечный круг заказов файла. Здесь же отказ уже пойман,
   * этот блок погашен, и никакой повторной отрисовки за ним не идёт: память
   * чистая ждёт СЛЕДУЮЩЕГО монтирования блока, которое закажет файл заново —
   * со своим правом на однократную перезагрузку (`loadPublicBlockChunk`).
   */
  componentDidCatch(error: unknown): void {
    const chunk = this.props.chunk;
    failure.delete(chunk);
    waiting.delete(chunk);
    // Н2: отчитываемся о куске ОДИН раз за визит — остальные блоки того же
    // куска падают по той же причине, и второй запрос ничего не добавляет.
    if (reported.has(chunk)) return;
    reported.add(chunk);
    const message = error instanceof Error ? error.message : String(error);
    reportClientCrash({
      // Имя куска в сообщении: по строке в «Пульсе» должно быть видно, ЧТО
      // именно не приехало, — иначе все куски выглядят одинаково.
      message: `[block-chunk:${chunk}] ${message}`,
      stack: error instanceof Error ? error.stack : undefined,
      url: typeof window === 'undefined' ? undefined : window.location.href,
    });
  }

  render(): React.ReactNode {
    // Пусто, а не надпись: текста для человека здесь нет вовсе, значит нет и
    // пары словарей — ошибку показывать посетителю воронки нечем и незачем.
    return this.state.failed ? null : this.props.children;
  }
}

export function PublicBlockRendererGate(
  props: PublicExtendedBlockProps,
): React.ReactElement | null {
  const chunk = publicBlockChunkOf(props.block.type);

  // Вид, которого в каталоге нет (воронка старше каталога, чужое поле), куска
  // НЕ заказывает: рисовать его всё равно некому — любой кусок дошёл бы до
  // своего `default` и вернул пусто. Раньше такой вид тянул с CDN весь
  // `extended` ради этой пустоты. Разметка при этом прежняя: пусто выдаётся
  // ВМЕСТЕ с общими обёртками блока — той же обёрткой, что ставят куски.
  if (!chunk) {
    return withExtendedBlockShell(
      props.block,
      null,
      props.followsExplicitSpacing,
    );
  }

  return (
    <PublicBlockChunkBoundary chunk={chunk}>
      <Suspense fallback={null}>
        <PublicBlockChunk chunk={chunk} {...props} />
      </Suspense>
    </PublicBlockChunkBoundary>
  );
}
